import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaClock, FaCheckCircle, FaHourglassHalf, FaCalendarAlt,
  FaArrowRight, FaFileAlt, FaUser, FaStar,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';

const STATUS_STYLES = {
  approved:  'text-emerald-600 bg-emerald-50',
  submitted: 'text-amber-600 bg-amber-50',
  draft:     'text-gray-500 bg-gray-100',
  rejected:  'text-red-600 bg-red-50',
};

function formatWeekRange(weekStart) {
  if (!weekStart) return '—';
  const start = new Date(weekStart);
  const end   = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}


export default function Dashboard() {
  const { user } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [stats, setStats]           = useState(null);
  const [holidays, setHolidays]     = useState([]);
  const [loading, setLoading]       = useState(true);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  useEffect(() => {
    Promise.all([api.getTimesheets(), api.getStats(), api.getUpcomingHolidays()])
      .then(([ts, s, h]) => { setTimesheets(ts); setStats(s); setHolidays(h); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const statCards = stats
    ? [
        { icon: <FaClock size={18} className="text-blue-500" />,        bg: 'bg-blue-50',    label: 'Hours This Week',      value: stats.hoursThisWeek.toFixed(1),   sub: 'Current week' },
        { icon: <FaHourglassHalf size={18} className="text-amber-500" />, bg: 'bg-amber-50',  label: 'Pending Timesheets',   value: stats.pendingCount,               sub: 'Awaiting approval' },
        { icon: <FaCheckCircle size={18} className="text-emerald-500" />, bg: 'bg-emerald-50',label: 'Approved This Month',  value: stats.approvedThisMonth,          sub: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }) },
        { icon: <FaCalendarAlt size={18} className="text-violet-500" />,  bg: 'bg-violet-50', label: 'Total Hours (YTD)',    value: stats.ytdHours.toFixed(1),        sub: `Jan – ${new Date().toLocaleString('default', { month: 'short' })} ${new Date().getFullYear()}` },
      ]
    : [
        { icon: <FaClock size={18} className="text-blue-500" />,          bg: 'bg-blue-50',    label: 'Hours This Week',     value: '—', sub: '' },
        { icon: <FaHourglassHalf size={18} className="text-amber-500" />, bg: 'bg-amber-50',   label: 'Pending Timesheets',  value: '—', sub: '' },
        { icon: <FaCheckCircle size={18} className="text-emerald-500" />, bg: 'bg-emerald-50', label: 'Approved This Month', value: '—', sub: '' },
        { icon: <FaCalendarAlt size={18} className="text-violet-500" />,  bg: 'bg-violet-50',  label: 'Total Hours (YTD)',   value: '—', sub: '' },
      ];

  return (
    <div className="bg-slate-50 min-h-screen">

      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#0c1b4d] to-[#1d4ed8] text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-blue-300 text-sm">{greeting()},</p>
              <h1 className="text-2xl font-bold mt-0.5">{user?.name}</h1>
              <p className="text-blue-200 text-sm mt-1">{user?.title} · {user?.department} · ID: {user?.employeeId}</p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/employee/timesheet"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                <FaClock size={13} /> Submit Timesheet
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className={`w-9 h-9 ${stat.bg} rounded-xl flex items-center justify-center mb-3`}>
                {stat.icon}
              </div>
              <div className="text-2xl font-bold text-[#0c1b4d]">{stat.value}</div>
              <div className="text-sm font-medium text-gray-700 mt-0.5">{stat.label}</div>
              <div className="text-xs text-gray-400 mt-0.5">{stat.sub}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Recent Timesheets */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="font-bold text-[#0c1b4d] flex items-center gap-2">
                  <FaFileAlt size={15} /> Recent Timesheets
                </h2>
                <Link to="/employee/timesheet" className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                  New Timesheet <FaArrowRight size={10} />
                </Link>
              </div>
              <div className="divide-y divide-gray-50">
                {loading && (
                  <div className="px-6 py-8 text-center text-gray-400 text-sm">Loading timesheets…</div>
                )}
                {!loading && timesheets.length === 0 && (
                  <div className="px-6 py-8 text-center text-gray-400 text-sm">No timesheets yet. Submit your first one!</div>
                )}
                {!loading && timesheets.slice(0, 8).map((ts) => {
                  const weekKey = ts.week_start ? ts.week_start.split('T')[0] : null;
                  const editLink = weekKey ? `/employee/timesheet?week=${weekKey}` : '/employee/timesheet';
                  return (
                  <div key={ts.id} className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50 transition-colors">
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-sm font-medium text-gray-800 truncate">{formatWeekRange(ts.week_start)}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{ts.total_hours}h logged</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-sm font-semibold text-gray-700">{parseFloat(ts.total_hours).toFixed(1)}h</span>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[ts.status] || 'text-gray-500 bg-gray-100'}`}>
                        {ts.status}
                      </span>
                      {ts.status === 'rejected' && (
                        <Link
                          to={editLink}
                          className="text-xs font-semibold px-2.5 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
                        >
                          Edit &amp; Resubmit
                        </Link>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-[#0c1b4d] text-sm mb-4">Quick Actions</h3>
              <div className="space-y-2">
                {[
                  { icon: <FaClock size={14} />,       label: 'Submit Timesheet',  to: '/employee/timesheet', color: 'text-blue-600 bg-blue-50 hover:bg-blue-100' },
                  { icon: <FaUser size={14} />,        label: 'View My Profile',   to: '#',                   color: 'text-violet-600 bg-violet-50 hover:bg-violet-100' },
                  { icon: <FaFileAlt size={14} />,     label: 'Timesheet History', to: '/employee/timesheet', color: 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' },
                  { icon: <FaCalendarAlt size={14} />, label: 'Request Time Off',  to: '/employee/leaves',    color: 'text-amber-600 bg-amber-50 hover:bg-amber-100' },
                ].map((action, i) => (
                  <Link key={i} to={action.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${action.color}`}>
                    {action.icon} {action.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Upcoming Holidays */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-[#0c1b4d] text-sm mb-4 flex items-center gap-2">
                <FaStar size={13} className="text-amber-400" /> Upcoming Holidays
              </h3>
              {holidays.length === 0 ? (
                <p className="text-xs text-gray-400">No upcoming holidays.</p>
              ) : (
                <div className="space-y-3">
                  {holidays.map(h => {
                    const d = new Date(h.date);
                    const daysAway = Math.ceil((d - new Date().setHours(0,0,0,0)) / (1000*60*60*24));
                    return (
                      <div key={h.id} className="border-l-2 border-amber-300 pl-3">
                        <p className="text-xs font-semibold text-[#0c1b4d]">{h.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                        </p>
                        <p className="text-xs text-amber-500 font-medium mt-0.5">
                          {daysAway === 0 ? 'Today!' : daysAway === 1 ? 'Tomorrow!' : `In ${daysAway} days`}
                        </p>
                        {h.description && <p className="text-xs text-gray-400 mt-0.5">{h.description}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
